<template>
  <div>
    <h5 class="text-center mb-2">Top 10 Cities</h5>
    <table class="table table-striped table-warning table-sm table-bordered">
      <thead>
      <tr>
        <th>City</th>
        <th>Visits</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(item, idx) in topCities" :key="idx">
        <td>{{ item.city }}</td>
        <td>{{ item.visits }}</td>
      </tr>
      </tbody>
    </table>
    <div v-if="loading">Loading...</div>
    <div v-if="error" class="text-danger">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';

const topCities = ref([]);
const loading = ref(false);
const error = ref(null);

onMounted(/**
 * Fetches and processes user's visited links data for identifying top visited cities.
 *
 * This asynchronous function retrieves the user's visited links data from the API,
 * counts visits per city, and populates a list of the top 10 most visited cities by the user.
 *
 * The function performs the following tasks:
 * - Sets a loading indicator to true at the beginning and reverts it to false at the end.
 * - Retrieves API URL and user's token from environment variables and session storage.
 * - Sends an authenticated GET request to the API to fetch visited link data for the user.
 * - Processes the response to count the number of visits per city.
 * - Sorts and selects the top 10 most visited cities based on visit counts.
 * - Handles possible errors during the API call by setting an error indicator.
 *
 * The resulting data structure for the top cities includes city name and visit count,
 * sorted in descending order of visits.
 *
 * @async
 * @function
 * @returns {Promise<void>} Resolves when the process is complete.
 */
async () => {
  loading.value = true;
  const apiUrl = process.env.VUE_APP_API_URL;
  const token = sessionStorage.getItem('token');
  try {
    const id = sessionStorage.getItem('id');
    const res = await axios.get(`${apiUrl}/linksvisited/id_user/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (res && Array.isArray(res.data.linksVisited)) {
      const counts = {};
      res.data.linksVisited.forEach(item => {
        const city = item?.city || 'Unknown';
        counts[city] = (counts[city] || 0) + 1;
      });
      topCities.value = Object.entries(counts)
          .map(([city, visits]) => ({ city, visits }))
          .sort((a, b) => b.visits - a.visits)
          .slice(0, 10);
    }
  } catch (e) {
    error.value = 'Failed to load city data';
  } finally {
    loading.value = false;
  }
});
</script>
